import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Stack, Card, CardContent, Button, TextField,
  Divider, Chip, Alert, CircularProgress, Skeleton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../../auth/AuthContext.js";
import { api } from "../../api/client.js";

interface Profile {
  user_id:          string;
  email:            string;
  tier:             string;
  created_at:       string;
  messages_limit:   number;
  chunks_limit:     number;
  has_subscription: boolean;
}

const TIER_LABEL: Record<string, string> = {
  free: "Free", starter: "Starter", pro: "Pro", enterprise: "Enterprise",
};
const TIER_COLOR: Record<string, "default" | "primary" | "secondary" | "success"> = {
  free: "default", starter: "primary", pro: "secondary", enterprise: "success",
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h5" mb={2}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 11, display: "block", mb: 0.25 }}>
      {children}
    </Typography>
  );
}

// ── Email change (two-step: update → verify code) ────────────────────────────

type EmailStep = "idle" | "editing" | "verifying" | "done";

function EmailField({ currentEmail, onUpdated }: { currentEmail: string; onUpdated: (email: string) => void }) {
  const { requestEmailChange, confirmEmailChange } = useAuth();
  const [step, setStep]       = useState<EmailStep>("idle");
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode]       = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const reset = () => { setStep("idle"); setNewEmail(""); setCode(""); setError(null); };

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || newEmail === currentEmail) return;
    setLoading(true); setError(null);
    try {
      await requestEmailChange(newEmail);
      setStep("verifying");
    } catch (err: any) {
      setError(err.message ?? "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) return;
    setLoading(true); setError(null);
    try {
      await confirmEmailChange(newEmail, code);
      setStep("done");
      onUpdated(newEmail);
    } catch (err: any) {
      setError(err.message ?? "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  if (step === "idle" || step === "done") {
    return (
      <Box mb={2}>
        <FieldLabel>Email</FieldLabel>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body1">{currentEmail}</Typography>
          {step === "done"
            ? <Typography variant="caption" color="success.main">Updated</Typography>
            : (
              <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                onClick={() => { setStep("editing"); setNewEmail(currentEmail); }}
                sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12 }}
              >
                Change
              </Button>
            )}
        </Stack>
      </Box>
    );
  }

  if (step === "editing") {
    return (
      <Box mb={2}>
        <FieldLabel>Email</FieldLabel>
        <form onSubmit={handleRequest}>
          <Stack spacing={1.5} sx={{ maxWidth: 380 }}>
            {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
            <TextField
              size="small" fullWidth type="email" autoFocus
              label="New email address"
              value={newEmail} onChange={e => setNewEmail(e.target.value)}
            />
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" size="small"
                disabled={!newEmail || newEmail === currentEmail || loading}
              >
                {loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Send verification code"}
              </Button>
              <Button size="small" onClick={reset} disabled={loading}>Cancel</Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    );
  }

  // verifying
  return (
    <Box mb={2}>
      <FieldLabel>Email</FieldLabel>
      <form onSubmit={handleVerify}>
        <Stack spacing={1.5} sx={{ maxWidth: 380 }}>
          <Alert severity="info" sx={{ py: 0.5 }}>
            A verification code was sent to <strong>{newEmail}</strong>. Enter it below to confirm.
          </Alert>
          {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
          <TextField
            size="small" fullWidth autoFocus
            label="Verification code"
            value={code} onChange={e => setCode(e.target.value)}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" size="small" disabled={!code || loading}>
              {loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Confirm"}
            </Button>
            <Button size="small" onClick={reset} disabled={loading}>Cancel</Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}

// ── Password change ───────────────────────────────────────────────────────────

function ChangePasswordForm() {
  const { changePassword } = useAuth();
  const [oldPw, setOldPw]     = useState("");
  const [newPw, setNewPw]     = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const valid = oldPw.length > 0 && newPw.length >= 8 && newPw === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true); setError(null); setSuccess(false);
    try {
      await changePassword(oldPw, newPw);
      setSuccess(true);
      setOldPw(""); setNewPw(""); setConfirm("");
    } catch (err: any) {
      setError(err.message ?? "Password change failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <Stack spacing={2} sx={{ maxWidth: 400 }}>
        {success && <Alert severity="success">Password updated successfully.</Alert>}
        {error   && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Current password" type="password" size="small" fullWidth
          value={oldPw} onChange={e => setOldPw(e.target.value)} autoComplete="current-password"
        />
        <TextField
          label="New password" type="password" size="small" fullWidth
          value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password"
          helperText="Minimum 8 characters"
          error={newPw.length > 0 && newPw.length < 8}
        />
        <TextField
          label="Confirm new password" type="password" size="small" fullWidth
          value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password"
          error={confirm.length > 0 && confirm !== newPw}
          helperText={confirm.length > 0 && confirm !== newPw ? "Passwords do not match" : ""}
        />
        <Box>
          <Button type="submit" variant="contained" disabled={!valid || loading} size="small">
            {loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Update Password"}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentEmail, setCurrentEmail] = useState<string>("");

  useEffect(() => {
    api.get<Profile>("/api/v1/dashboard/settings")
      .then(p => { setProfile(p); setCurrentEmail(p.email); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <Box maxWidth={680}>
      <Typography variant="h2" mb={0.5}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage your account and preferences.
      </Typography>

      {/* ── Account ── */}
      <Section title="Account">
        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="text" width={240} />
            <Skeleton variant="text" width={180} />
          </Stack>
        ) : (
          <>
            <EmailField currentEmail={currentEmail} onUpdated={setCurrentEmail} />
            {memberSince && (
              <Box>
                <FieldLabel>Member Since</FieldLabel>
                <Typography variant="body1">{memberSince}</Typography>
              </Box>
            )}
          </>
        )}
      </Section>

      {/* ── Plan ── */}
      <Section title="Plan">
        {loading ? (
          <Skeleton variant="rounded" width={160} height={36} />
        ) : profile && (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Chip
                label={TIER_LABEL[profile.tier] ?? profile.tier}
                color={TIER_COLOR[profile.tier] ?? "default"}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {profile.messages_limit.toLocaleString()} messages/mo ·{" "}
                {profile.chunks_limit >= 1_000_000
                  ? "Unlimited"
                  : profile.chunks_limit.toLocaleString()}{" "}
                chunks
              </Typography>
            </Stack>
            <Box>
              <Button variant="outlined" size="small" onClick={() => nav("/dashboard/billing")}>
                {profile.has_subscription ? "Manage Subscription" : "Upgrade Plan"}
              </Button>
            </Box>
          </Stack>
        )}
      </Section>

      {/* ── Security ── */}
      <Section title="Security">
        <Typography variant="body2" color="text.secondary" mb={2}>
          Change your account password. You'll need your current password to confirm.
        </Typography>
        <ChangePasswordForm />
      </Section>

      {/* ── Session ── */}
      <Section title="Session">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2">Signed in as <strong>{user?.email}</strong></Typography>
            <Typography variant="caption" color="text.secondary">
              You'll be returned to the login screen.
            </Typography>
          </Box>
          <Button
            variant="outlined" color="error" size="small" startIcon={<LogoutIcon />}
            onClick={() => { logout(); nav("/login"); }}
          >
            Sign Out
          </Button>
        </Stack>
      </Section>
    </Box>
  );
}
